import React, { useState, useEffect } from 'react';
import { Container, IconButton, Typography, MenuItem, Select, FormControl, InputLabel, Box, Stack, Menu } from '@mui/material';
import { useChatsData } from './ChatsData';
import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import SearchBar from '../../components/Search.js';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Loader from '../../loader.js';
import '../../Calendar.css'; 
import MiniDrawer from '../../components/MiniDrawer.js';
import { useTheme } from '@mui/material/styles';
import useAlert from '../../alert/useAlert.js';
import AlertDialog from '../../Dialog.js';
import ChatScreen from './ChatScreen.js';
import { formatDistanceToNowStrict, isToday } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MessageIcon from '@mui/icons-material/Message';
// AI ("Text to Speech") chat and Broadcast are commented out for now — they were wired to the
// old project + the now-defunct WhatsApp backend. Left in place to re-enable later.
// import AIChatScreen from './AIChatScreen.js';
// import AIChatScreenAzure from './AIChatScreenAzure.js';
// import BroadcastComponent from './BroadcastComponent.js';

const Chats = () => {
  const { TEAM_PHONE_NUMBER, contactList, userDetails, textMessages, insertTextMessage, updateTextMessage, deleteTextMessage, subscriptionActiveStatus, currentOrg, messagesLoading } = useChatsData();
  const options = [{ value: 'Pending', label: 'Pending' }, { value: 'All', label: 'All' }, { value: 'Completed', label: 'Completed' }];
  const [view, setView] = useState('Pending');
  const [sortOrder, setSortOrder] = useState('Newest');
  const [searchText, setSearchText] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [sortedContacts, setSortedContacts] = useState([])
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState({ open: false, content: '', confirmAction: null });
  const [selectedContact, setSelectedContact] = useState('');
  const [showAIChat, setShowAIChat] = useState(false);
  const [showFollowupAgentChat, setShowFollowupAgentChat] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const sortedContacts = [...contactList].sort((a, b) => {
      // Get most recent message dates for both patients
      const aMostRecentDate = getMostRecentDate(a);
      const bMostRecentDate = getMostRecentDate(b);
  
      // If there are no messages, treat the date as the earliest possible date
      const aDate = aMostRecentDate || new Date(0);
      const bDate = bMostRecentDate || new Date(0);
  
      // Sort based on the sortOrder
      if (sortOrder === 'Newest') {
        return bDate - aDate;  // Newest first
      } else {
        return aDate - bDate;  // Oldest first
      }
    });
    setView('All');
    setSortedContacts(sortedContacts);
    setFilteredItems(sortedContacts);
  }, [sortOrder, contactList, textMessages]);

  const handleOpenBroadcast = () => {
    setSelectedContact(null);
    setShowAIChat(false);
    setShowFollowupAgentChat(false);
    setShowBroadcast(true);
  };

  const handleCloseBroadcast = () => {
    setShowBroadcast(false);
  };

  const handleSortChange = (value) => {
    setSearchText('');
    setSortOrder(value);
  };

  const handleViewChange = (value) => {
    setSearchText('');
    if(view !== value){
      const filtered = value === 'Pending' ? sortedContacts.filter((contact) => 
          textMessages.filter((item) => item.chat_room_id === contact).some(item => item.ispinned)
        )
        :
        value === 'Completed' ? sortedContacts.filter((contact) => 
          textMessages.filter((item) => item.chat_room_id === contact).every(item => !item.ispinned)
        )
        :
        sortedContacts;
      setView(value);
      setFilteredItems(filtered);
    }
    setTimeout(() => setLoading(false), 500);
  };

  // Conversations are keyed by the patient's phone number (wa_messages.chat_room_id).
  const getContactTitle = (id) => id ?? '';

  const handleSearchChange = (event) => {
    const searchValue = event.target.value.toLowerCase();
    setSearchText(searchValue);
    const filtered =
      view === 'Consultations List'
        ? ''
        : sortedContacts.filter((contact) => {
            const title = getContactTitle(contact).toLowerCase();
            return (
              title.includes(searchValue) ||
              contact.toLowerCase().includes(searchValue)
            );
          });
    setFilteredItems(filtered);
  };

  const getMostRecentDate = (id) => {
    if (textMessages.length === 0) return null;
    const sortedMessages = textMessages.filter((item) => item.chat_room_id === id).sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    if(sortedMessages.length === 0) return null;
    return new Date(new Date(sortedMessages[0].created_at).getTime() - (8 * 60 * 60 * 1000));
  };

  const displayDate = (id) => {
    const mostRecentDate = getMostRecentDate(id);
    if (!mostRecentDate) return '';
    if (isToday(mostRecentDate)) return 'Today';
    return `${formatDistanceToNowStrict(mostRecentDate)} ago`;
  };

  const displayMessage = (id) => {
    const sortedMessages = textMessages.filter((item) => item.chat_room_id === id).sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    if(sortedMessages.length === 0) return '';
    const message = sortedMessages[0].message;
    const cleanedMessage = message.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\n/g, ' ');
    const truncatedMessage = cleanedMessage.slice(0, 18); 
    return cleanedMessage.length > 18  ? `${truncatedMessage}...` : truncatedMessage;
  };

  const handleCloseChat = () => {
    setSelectedContact(null);
    setView("All");
  };

  const handleOpenAIChat = () => {
    setSelectedContact(null);
    setShowAIChat(true);
    setShowFollowupAgentChat(false);
    setShowBroadcast(false);
  };

  const handleCloseAIChat = () => {
    setShowAIChat(false);
  };

  const handleFollowUpAgentChat = () => {
    setSelectedContact(null);
    setShowFollowupAgentChat(true);
    setShowAIChat(false);
    setShowBroadcast(false);
  };

  const handleCloseFollowupAgentChat = () => {
    setShowFollowupAgentChat(false);
  };

  // Empty state component
  const EmptyState = () => {
    const getEmptyStateMessage = () => {
      if (searchText) {
        return {
          title: "No contacts found",
          subtitle: "Try adjusting your search terms or check back later"
        };
      }
      
      switch (view) {
        case 'Pending':
          return {
            title: "No pending conversations",
            subtitle: "All conversations have been completed"
          };
        case 'Completed':
          return {
            title: "No completed conversations",
            subtitle: "Completed conversations will appear here"
          };
        default:
          return {
            title: "No messages exchanged yet",
            subtitle: "When you start exchanging messages, your conversations will appear here"
          };
      }
    };

    const { title, subtitle } = getEmptyStateMessage();

    return (
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '60px 40px',
          textAlign: 'center',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          border: '2px dashed #E0E0E0',
          minHeight: '300px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#F0F9FF',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '24px',
          }}
        >
          <MessageIcon
            sx={{
              fontSize: '40px',
              color: '#29C0BB',
            }}
          />
        </Box>
        
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            color: '#333333',
            marginBottom: '12px',
          }}
        >
          {title}
        </Typography>
        
        <Typography
          variant="body1"
          sx={{
            color: '#666666',
            maxWidth: '400px',
            lineHeight: 1.5,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    );
  };

  return (
    <Container disableGutters maxWidth={false}>
      <Grid container sx={{overflow: 'auto', minHeight: '100vh', minWidth: '100vw'}}>
        <Grid size={{xs: 1, md: 0}}>
          <MiniDrawer />
        </Grid>
        <Grid size="grow">
          {messagesLoading && (
            <Box sx={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(255,255,255,0.75)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Loader />
            </Box>
          )}
          <Grid container spacing={2} sx={{ backgroundColor: '#F8F9FA', display: 'flex', flexDirection: 'column',  width: '100%', height: '100%' }}>
            <Grid size={12}>
              <div style={{ padding: '24px 5vw 24px 8vw' }}>
                <Grid container spacing={2} sx={{ flexDirection: { xs: "column", md: "row"}   }} justifyContent="space-between">
                  <Grid size={{xs: 12, md: (selectedContact && selectedContact.length !== 0) || showAIChat ? 5 : 12 }}>
                    <Grid container direction="column">
                      <Grid size="auto" sx={{marginBottom: '20px'}}>
                        {options.map((option) => (
                            <Button
                            key={option.value}
                            onClick={() => { setLoading(true); handleViewChange(option.value); }}
                            sx={{
                                flex: 1,
                                textTransform: 'none',
                                borderBottom: view === option.value ? '3px solid #29C0BB' : 'none',
                                paddingRight: '20px',
                                paddingTop: '8px',
                                color: '#000000',
                                fontWeight: 'bold',
                            }}
                            >
                            {option.label}
                            </Button>
                        ))}
                      </Grid>
                      <Grid size="grow" sx={{marginBottom: '30px'}}>
                        <SearchBar bgcolor='#FFFFFF' placeholder="Search for a number" width="30vw" value={searchText} onChange={handleSearchChange}/>
                      </Grid>
                      <Grid size="auto" sx={{marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}> 
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ mr: 1 }}>Sort By:</Typography>
                          <Button
                            endIcon={<ArrowDropDownIcon />}
                            onClick={(event) => setAnchorEl(event.currentTarget)}
                            sx={{ 
                              textTransform: 'none', 
                              minWidth: 'auto',
                              color: 'black',
                              fontWeight: 'bold'
                            }}
                          >
                            {sortOrder}
                          </Button>
                          <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={() => setAnchorEl(null)}
                          >
                            <MenuItem onClick={() => {handleSortChange('Newest'); setAnchorEl(null)}}>Newest</MenuItem>
                            <MenuItem onClick={() => {handleSortChange('Oldest'); setAnchorEl(null)}}>Oldest</MenuItem>
                          </Menu>
                        </Box>
                        {/*<Stack direction="row" spacing={2}>
                          <Box>
                            <IconButton 
                              onClick={handleOpenBroadcast}
                              sx={{
                                backgroundColor: showBroadcast ? '#29C0BB' : 'transparent',
                                color: showBroadcast ? 'white' : '#29C0BB',
                                border: '2px solid #29C0BB',
                                borderRadius: '4px',
                                padding: '4px 6px',
                                fontSize: '18px',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor: '#29C0BB',
                                  color: 'white'
                                }
                              }}
                            >
                              Broadcast Message
                            </IconButton>
                          </Box>
                          <Box>
                            <IconButton 
                              onClick={handleOpenAIChat}
                              sx={{
                                backgroundColor: showAIChat ? '#29C0BB' : 'transparent',
                                color: showAIChat ? 'white' : '#29C0BB',
                                border: '2px solid #29C0BB',
                                borderRadius: '4px', // Match the default button border radius
                                padding: '4px 6px',
                                fontSize: '18px',
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: '#29C0BB',
                                    color: 'white'
                                }
                              }}
                            >
                              Text to Speech
                            </IconButton>
                          </Box>
                          {/* <Box>
                            <IconButton 
                              onClick={handleFollowUpAgentChat}
                              sx={{
                                backgroundColor: showAIChat ? '#29C0BB' : 'transparent',
                                color: showAIChat ? 'white' : '#29C0BB',
                                border: '2px solid #29C0BB',
                                borderRadius: '4px', // Match the default button border radius
                                padding: '4px 6px',
                                fontSize: '18px',
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: '#29C0BB',
                                    color: 'white'
                                }
                              }}
                            >
                              Followup Agent
                            </IconButton>
                          </Box> 
                        </Stack>*/}
                      </Grid>
                      {
                        loading ? 
                        <Grid container spacing={2} sx={{ padding: '50px', height: '50vh' }} justifyContent="center" alignItems="center">
                          <Loader /> 
                        </Grid>
                        :
                          (
                            <Grid size="auto"
                                  sx={{
                                    maxHeight: 'calc(100vh - 260px)', // keep list visible
                                    overflowY: 'auto',
                                    pr: 1,
                                    minWidth: 0
                                  }}>
                              {filteredItems.length === 0 ? (
                                <EmptyState />
                              ) : (
                                filteredItems.map((contact) => (
                                  <Grid container key={contact}
                                    onClick={() => { setSelectedContact(contact); setShowAIChat(false); } }
                                    sx={{
                                      cursor: 'pointer',
                                      alignItems: 'center',
                                      '&:hover': { border: "3px solid #29C0BB" },
                                      border: contact === selectedContact ? "3px solid #29C0BB" : null,
                                      backgroundColor: '#FFFFFF',
                                      borderRadius: '10px',
                                      padding: '10px 16px',
                                      marginBottom: '20px',
                                      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                                      minWidth: 0
                                    }}>
                                  <Grid size={2}>
                                    <IconButton
                                      size="large"
                                      edge="end"
                                      >
                                      <AccountCircle sx={{ fontSize: '40px', marginRight: '10px' }}/>
                                    </IconButton>
                                  </Grid>
                                  <Grid size={7} sx={{flexGrow: 1}}>
                                    <Typography variant="body1" sx={{fontWeight: 'bold', fontSize: '18px', marginBottom: '10px'}}>
                                      {getContactTitle(contact)}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" fullWidth>
                                      {displayMessage(contact)}
                                    </Typography>
                                  </Grid>
                                  <Grid size={3} align="right">
                                    <Typography variant="body2" color="textSecondary" sx={{marginRight: '10px', marginBottom: '10px'}}>
                                      {displayDate(contact)}
                                    </Typography>
                                    {/* {
                                      contact === "85293774519" ?
                                      <Box sx={{ padding: '5px 15px', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', borderRadius: 5, backgroundColor: '#F6D3D5', color: "#F90606", cursor: 'pointer'}}>
                                        <Typography variant="h7">High</Typography>
                                      </Box>
                                      :
                                      <Box sx={{ padding: '5px 15px', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', borderRadius: 5, backgroundColor: '#DAF4E6', color: "#07B557", cursor: 'pointer'}}>
                                        <Typography variant="h7">Low</Typography>
                                      </Box>
                                    } */}
                                  </Grid>
                                </Grid>
                              ))
                            )}
                          </Grid>                 
                        )
                      }
                    </Grid>
                  </Grid>
                  {
                    selectedContact && selectedContact.length !== 0 && (
                    <Grid size={{xs: 12, md: 7}}>
                      <ChatScreen onClose={handleCloseChat} phoneNumber={TEAM_PHONE_NUMBER} userDetails={userDetails} selectedContact={selectedContact} textMessages={textMessages} insertTextMessage={insertTextMessage} updateTextMessage={updateTextMessage} deleteTextMessage={deleteTextMessage}/>
                    </Grid>
                    )
                  }
                  {/* AI ("Text to Speech") chat panel commented out for now (see imports above)
                  {
                    showAIChat && (
                    <Grid size={12}>
                      <AIChatScreenAzure
                        onClose={handleCloseAIChat}
                        teamId={currentOrg?.id}
                        textMessages={textMessages}
                        insertTextMessage={insertTextMessage}
                        deleteTextMessage={deleteTextMessage}
                      />
                    </Grid>
                    )
                  }
                  */}
                  {/* {
                    showFollowupAgentChat && (
                      <Grid size={{xs: 12, md: 7}}>
                        <FollowupAgentChatScreen 
                          onClose={handleCloseFollowupAgentChat} 
                          teamId={currentOrg?.id} 
                          textMessages={textMessages} 
                          insertTextMessage={insertTextMessage} 
                          deleteTextMessage={deleteTextMessage}
                        />
                      </Grid>
                    )
                  } */}
                  {/* Broadcast panel commented out for now (see imports above)
                  {
                    showBroadcast && (
                    <Grid size={12}>
                      <BroadcastComponent
                        onClose={handleCloseBroadcast}
                        teamId={currentOrg?.id}
                        TEAM_PHONE_NUMBER={TEAM_PHONE_NUMBER}
                        sortedContacts={sortedContacts}
                      />
                    </Grid>
                    )
                  }
                  */}
                  {
                    openDialog && (
                      <AlertDialog data={{open: openDialog.open, content: openDialog.content, confirmAction: openDialog.confirmAction, setOpen: setOpenDialog}}/>
                    )
                  }
                </Grid>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {(subscriptionActiveStatus !== false && userDetails && !userDetails.mode) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.2)',
              border: '2px solid #29C0BB',
            }}
          >
            <Box
              sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#E0F7FA',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0 auto 20px auto',
              }}
            >
              <Typography
                sx={{
                  fontSize: '36px',
                  color: '#29C0BB',
                }}
              >
                ⚙️
              </Typography>
            </Box>
            
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: '#333333',
                marginBottom: '16px',
              }}
            >
              Profile Setup Required
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: '#666666',
                marginBottom: '32px',
                lineHeight: 1.6,
              }}
            >
              Your profile setup is incomplete. To continue using the application, please select your User Mode in Profile Settings.
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#29C0BB',
                  color: '#FFFFFF',
                  borderRadius: '30px',
                  padding: '12px 24px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#259B96',
                  },
                }}
                onClick={() => {
                  navigate('/settings');
                }}
              >
                Go to Settings
              </Button>
            </Stack>
          </Box>
        </Box>
      )}
      {subscriptionActiveStatus === false && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.2)',
              border: '2px solid #FF6B6B',
            }}
          >
            <Box
              sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#FFE6E6',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0 auto 20px auto',
              }}
            >
              <Typography
                sx={{
                  fontSize: '36px',
                  color: '#FF6B6B',
                }}
              >
                ⚠️
              </Typography>
            </Box>
            
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: '#333333',
                marginBottom: '16px',
              }}
            >
              Subscription Inactive
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: '#666666',
                marginBottom: '32px',
                lineHeight: 1.6,
              }}
            >
              Your subscription has expired and access to our platform is currently suspended. 
              To continue using our services and manage your patients, please reactivate your subscription from the settings.
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#29C0BB',
                  color: '#FFFFFF',
                  borderRadius: '30px',
                  padding: '12px 24px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#259B96',
                  },
                }}
                onClick={() => {
                  // Navigate to settings page - you might need to import useNavigate from react-router-dom
                  navigate('/settings');
                }}
              >
                Subscribe Now
              </Button>
            </Stack>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default Chats;